import { isEqual } from 'lodash-es';
import { useCallback } from 'react';

import { MultimediaNotification } from '../datasource/generated';
import { useGetAlertsAndAnnouncementsQuery } from '../datasource/queries/cmsMessages';
import { addAlert, addAnnouncement, Announcement, CustomToastType, removeToast,Toast } from '../stories/Store/toastSlice';
import { formatErrorMessage } from '../utility/toastUtils';
import { useAppDispatch, useAppStore } from '.';

function htmlDecode(input: string) {
    const doc = new DOMParser()
        .parseFromString(input, 'text/html');
    return doc.documentElement.textContent ?? '';
}

export const useToasts = () => {
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const { data: alertsAndAnnoncements } = useGetAlertsAndAnnouncementsQuery();

    const addToastsFromNotifications = useCallback((notifications: MultimediaNotification[]) => {
        const parsed: (Announcement | null)[] = notifications.map(notification => {
            const availableDate = Date.parse(notification.availableDate ?? '');
            const expirationDate = Date.parse(notification.expirationDate ?? '');

            const timeToAvailableDateInMs = Date.now() - availableDate;
            const isAfterAvailableDate = timeToAvailableDateInMs >= 0;
        
            const timeToExpirationDateInMs = expirationDate - Date.now();
            const isBeforeExpirationDate = timeToExpirationDateInMs >= 0;
        
            let expirationTimeoutId = 0;
            if (isBeforeExpirationDate) {
                expirationTimeoutId = setTimeout(
                    () => {
                        dispatch(removeToast(notification.notificationId!));
                    },
                    Math.abs(timeToExpirationDateInMs)) as unknown as number;
            }
            
            let availableTimeoutId = 0;
            if (isBeforeExpirationDate && !isAfterAvailableDate) {
                availableTimeoutId = setTimeout(
                    () => dispatch(addAnnouncement({ 
                        id: notification.notificationId!, 
                        contentType: 'html', 
                        htmlContent: htmlDecode(notification.notificationText ?? ''), 
                        expirationTimeoutId: expirationTimeoutId,
                    })),
                    Math.abs(timeToAvailableDateInMs)) as unknown as number;
            }
            
            const shouldShowAnnouncement = isAfterAvailableDate && isBeforeExpirationDate;
            if (shouldShowAnnouncement) {
                return { 
                    id: notification.notificationId!, 
                    contentType: 'html' as const, 
                    htmlContent: htmlDecode(notification.notificationText ?? ''), 
                    cta: undefined,
                    message: '',
                    type: 'announcement' as const, 
                    availableTimeoutId: availableTimeoutId,
                    expirationTimeoutId: expirationTimeoutId,
                };
            }
            
            return null;
        });
        
        const filtered = parsed.filter((toast): toast is Toast => !!toast);
        
        filtered.map(t => dispatch(addAnnouncement(t)));
    },
    [dispatch]);

    
    const showAlertToast = useCallback(async (event: { code: number } | { custom: CustomToastType }) => {
        let message = 'Something wrong happened';
        let customType: CustomToastType | undefined = undefined;
        if ('code' in event) {
            switch (event.code) {
                case 221000:
                case 230002:
                case 232002:
                    message = formatErrorMessage(alertsAndAnnoncements?.internetConnectionErrorMessage ?? message, String(event.code));
                    break;
            
                default:
                    message = formatErrorMessage(alertsAndAnnoncements?.defaultJWPlayerErrorMessage ?? message, String(event.code));
            }
        } else if ('custom' in event) {
            customType = event.custom;
            switch (event.custom) {
                case CustomToastType.SeekFailed:
                    message = formatErrorMessage(alertsAndAnnoncements?.seekFailedErrorMessage ?? message);
                    break;
            }
        }

        dispatch(addAlert({ contentType: 'html', htmlContent: message, customType }));
    }, [
        alertsAndAnnoncements?.defaultJWPlayerErrorMessage,
        alertsAndAnnoncements?.internetConnectionErrorMessage,
        alertsAndAnnoncements?.seekFailedErrorMessage,
        dispatch
    ]);

    type FilterableToastProperties = Omit<Toast, `${string}TimeoutId` | 'id'>;
    /** Clear toasts by toastId or a combination of toast properties */
    const clearToasts = useCallback((filterOrId: Partial<FilterableToastProperties> | Toast['id']) => {
        if (typeof filterOrId === 'number' || typeof filterOrId === 'string') {
            dispatch(removeToast(filterOrId));
            return;
        }
        
        const toasts = store.getState().toast.toasts;

        const filteredToasts = toasts.filter(t => {
            for (const key in filterOrId) {
                const toastValue = t[key as keyof FilterableToastProperties];
                const filterValue = filterOrId[key as keyof FilterableToastProperties];
                switch (typeof toastValue) {
                    case 'string':
                        if (toastValue.toLowerCase() !== String(filterValue).toLowerCase())
                            return false;
                        break;
                    case 'number':
                    case 'boolean':
                        if (toastValue !== filterValue)
                            return false;
                        break;
                    default:
                        if (!isEqual(toastValue, filterValue))
                            return false;
                }
            }

            return true;
        });

        for (const toast of filteredToasts) {
            dispatch(removeToast(toast.id));
        }
    }, [dispatch, store]);
            
    return {
        addToastsFromNotifications,
        showAlertToast,
        clearToasts,
    };
};
